import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "@/pages/auth/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const next = sp.get("next") || "/onboarding";
  const [f, setF] = useState({ email: "", username: "", password: "" });
  const [age, setAge] = useState(false);
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setError("");
    if (!age) { setError("Devi dichiarare di essere maggiorenne per registrarti."); return; }
    if (!terms) { setError("Devi accettare i Termini di servizio."); return; }
    if (!privacy) { setError("Devi confermare la presa visione dell'informativa privacy."); return; }
    setLoading(true);
    try {
      const res = await register({ ...f, age_declared: age, accept_terms: terms, privacy_seen: privacy });
      if (res.email_sent) toast.success("Ti abbiamo inviato un'email per verificare l'indirizzo.");
      else toast.info("Registrazione completata. La verifica email verrà attivata a breve.");
      nav(next);
    } catch (err) { setError(apiError(err, "Registrazione non riuscita.")); }
    finally { setLoading(false); }
  };

  return (
    <AuthShell title="Crea il tuo account" subtitle="Iscrizione gratuita · Riservata ai maggiorenni"
      footer={<>Hai già un account? <Link to="/accedi" className="text-[#2DD4BF] hover:underline">Accedi</Link></>}>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {error && <div role="alert" data-testid="register-error" className="flex items-center gap-2 rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/10 px-3 py-2 text-sm text-[#FCA5A5]"><AlertCircle className="h-4 w-4 shrink-0" />{error}</div>}
        <div>
          <Label htmlFor="email" className="text-[#CBD5E1]">Email (privata)</Label>
          <Input id="email" data-testid="register-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-[#0B1020] border-[#2A3655]" />
        </div>
        <div>
          <Label htmlFor="username" className="text-[#CBD5E1]">Username</Label>
          <Input id="username" data-testid="register-username" required value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className="bg-[#0B1020] border-[#2A3655]" />
          <p className="mt-1 text-xs text-[#64748B]">3-20 caratteri. Non fa distinzione tra maiuscole e minuscole.</p>
        </div>
        <div>
          <Label htmlFor="password" className="text-[#CBD5E1]">Password</Label>
          <Input id="password" data-testid="register-password" type="password" required minLength={8} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="bg-[#0B1020] border-[#2A3655]" />
          <p className="mt-1 text-xs text-[#64748B]">Almeno 8 caratteri.</p>
        </div>
        <div className="space-y-3 rounded-lg border border-[#2A3655] bg-[#0B1020] p-3">
          <label className="flex items-start gap-2 text-sm text-[#CBD5E1]">
            <Checkbox data-testid="register-age" checked={age} onCheckedChange={setAge} className="mt-0.5 border-[#2A3655] data-[state=checked]:bg-[#7C3AED]" />
            <span>Dichiaro di avere <b className="text-[#F8FAFC]">almeno 18 anni</b>.</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-[#CBD5E1]">
            <Checkbox data-testid="register-terms" checked={terms} onCheckedChange={setTerms} className="mt-0.5 border-[#2A3655] data-[state=checked]:bg-[#7C3AED]" />
            <span>Accetto i <Link to="/termini" target="_blank" className="text-[#2DD4BF] hover:underline">Termini di servizio</Link> (v1.0).</span>
          </label>
          <label className="flex items-start gap-2 text-sm text-[#CBD5E1]">
            <Checkbox data-testid="register-privacy" checked={privacy} onCheckedChange={setPrivacy} className="mt-0.5 border-[#2A3655] data-[state=checked]:bg-[#7C3AED]" />
            <span>Ho preso visione dell'<Link to="/privacy" target="_blank" className="text-[#2DD4BF] hover:underline">informativa privacy</Link>.</span>
          </label>
        </div>
        <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full bg-[#7C3AED] hover:bg-[#6D28D9]">{loading ? "Creazione…" : "Registrati"}</Button>
      </form>
    </AuthShell>
  );
}
